import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useSelector } from 'react-redux'
import { defineMessages, useIntl } from 'react-intl'

// Components
import { Text, If } from '../..'
import { IconArrowRightBig } from '../Icon'

// Selectors
import {
  selectCurrentZipcode,
  selectCurrentMarketIndexLatestDelta,
  selectLatestMarketValue
} from '../../../store/selectors/marketRecord'

// Utils
import { MARKET_DEMAND_KEYS, MARKET_VALUE_BREAK_POINTS } from '../../../constants/marketRecords'

// Styles
import styles from './MarketSpotlightText.module.scss'

export const CHANGE_BREAK = 1.5
export const RAPID_CHANGE_BREAK = 3.5

const TEMPERATURES = MARKET_DEMAND_KEYS

const VECTORS = {
  UP_QUICKLY: 'upQuickly',
  UP_NORMAL: 'upNormal',
  BALANCED: 'balanced',
  DOWN_NORMAL: 'downNormal',
  DOWN_QUICKLY: 'downQuickly'
}

const CURRENT_MARKET_ICONS = {
  [TEMPERATURES.VERY_HIGH]: '📈',
  [TEMPERATURES.HIGH]: '📈',
  [TEMPERATURES.MODERATE]: '✅',
  [TEMPERATURES.LOW]: '❄️',
  [TEMPERATURES.VERY_LOW]: '❄️'
}

export const directionMessages = defineMessages({
  high_balanced: {
    id: 'MarketSpotlight.temperature.status.high.balanced',
    defaultMessage: 'is in demand and holding steady'
  },
  high_downNormal: {
    id: 'MarketSpotlight.temperature.status.high.downNormal',
    defaultMessage: 'is in demand but cooling down a bit'
  },
  high_downQuickly: {
    id: 'MarketSpotlight.temperature.status.high.downQuickly',
    defaultMessage: 'is in demand but cooling down quickly'
  },
  high_upNormal: {
    id: 'MarketSpotlight.temperature.status.high.upNormal',
    defaultMessage: 'is in demand and picking up'
  },
  high_upQuickly: {
    id: 'MarketSpotlight.temperature.status.high.upQuickly',
    defaultMessage: 'is in demand and picking up quickly'
  },
  low_balanced: {
    id: 'MarketSpotlight.temperature.status.low.balanced',
    defaultMessage: 'is cool but holding steady'
  },
  low_downNormal: {
    id: 'MarketSpotlight.temperature.status.low.downNormal',
    defaultMessage: 'is cool and getting a bit colder'
  },
  low_downQuickly: {
    id: 'MarketSpotlight.temperature.status.low.downQuickly',
    defaultMessage: 'is cool and getting colder quickly'
  },
  low_upNormal: {
    id: 'MarketSpotlight.temperature.status.low.upNormal',
    defaultMessage: 'is cool but picking up'
  },
  low_upQuickly: {
    id: 'MarketSpotlight.temperature.status.low.upQuickly',
    defaultMessage: 'is cool but picking up quickly'
  },
  moderate_balanced: {
    id: 'MarketSpotlight.temperature.status.moderate.balanced',
    defaultMessage: 'is balanced and holding steady'
  },
  moderate_downNormal: {
    id: 'MarketSpotlight.temperature.status.moderate.downNormal',
    defaultMessage: 'is balanced but cooling down a bit'
  },
  moderate_downQuickly: {
    id: 'MarketSpotlight.temperature.status.moderate.downQuickly',
    defaultMessage: 'is balanced but cooling down quickly'
  },
  moderate_upNormal: {
    id: 'MarketSpotlight.temperature.status.moderate.upNormal',
    defaultMessage: 'is balanced but picking up'
  },
  moderate_upQuickly: {
    id: 'MarketSpotlight.temperature.status.moderate.upQuickly',
    defaultMessage: 'is balanced but picking up quickly'
  },
  veryHigh_balanced: {
    id: 'MarketSpotlight.temperature.status.veryHigh.balanced',
    defaultMessage: 'is in high demand and holding steady'
  },
  veryHigh_downNormal: {
    id: 'MarketSpotlight.temperature.status.veryHigh.downNormal',
    defaultMessage: 'is in high demand but cooling down a bit'
  },
  veryHigh_downQuickly: {
    id: 'MarketSpotlight.temperature.status.veryHigh.downQuickly',
    defaultMessage: 'is in high demand but cooling down quickly'
  },
  veryHigh_upNormal: {
    id: 'MarketSpotlight.temperature.status.veryHigh.upNormal',
    defaultMessage: 'is in high demand and picking up'
  },
  veryHigh_upQuickly: {
    id: 'MarketSpotlight.temperature.status.veryHigh.upQuickly',
    defaultMessage: 'is in high demand and picking up quickly'
  },
  veryLow_balanced: {
    id: 'MarketSpotlight.temperature.status.veryLow.balanced',
    defaultMessage: 'is cold and holding steady'
  },
  veryLow_downNormal: {
    id: 'MarketSpotlight.temperature.status.veryLow.downNormal',
    defaultMessage: 'is cold and getting a bit colder'
  },
  veryLow_downQuickly: {
    id: 'MarketSpotlight.temperature.status.veryLow.downQuickly',
    defaultMessage: 'is cold and getting colder quickly'
  },
  veryLow_upNormal: {
    id: 'MarketSpotlight.temperature.status.veryLow.upNormal',
    defaultMessage: 'is cold but picking up'
  },
  veryLow_upQuickly: {
    id: 'MarketSpotlight.temperature.status.veryLow.upQuickly',
    defaultMessage: 'is cold but picking up quickly'
  }
})

export const descriptionMessages = defineMessages({
  high: {
    id: 'MarketSpotlight.temperature.description.up',
    defaultMessage: 'An in demand market means you may be able to sell your home faster and for more money'
  },
  low: {
    id: 'MarketSpotlight.temperature.description.down',
    defaultMessage: 'A cooler market means it might take more time to sell your home and possibly for less money'
  },
  moderate: {
    id: 'MarketSpotlight.temperature.description.balanced',
    defaultMessage:
      'A balanced market has decent inventory and a healthy amount of buyers - you could likely sell your home on an average timeline for a moderate amount.'
  }
})

const MarketSpotlightText = ({ withDescription, className }) => {
  const intl = useIntl()

  const zip = useSelector(selectCurrentZipcode)
  const latestDelta = useSelector(selectCurrentMarketIndexLatestDelta)
  const latestMarketValue = useSelector(selectLatestMarketValue)

  if (!zip || Number.isNaN(latestDelta) || Number.isNaN(latestMarketValue)) {
    return null
  }

  let description
  let rate
  let current

  if (latestMarketValue > MARKET_VALUE_BREAK_POINTS.VERY_HIGH) {
    current = TEMPERATURES.VERY_HIGH
    description = descriptionMessages.high
  } else if (latestMarketValue > MARKET_VALUE_BREAK_POINTS.HIGH) {
    current = TEMPERATURES.HIGH
    description = descriptionMessages.high
  } else if (latestMarketValue > MARKET_VALUE_BREAK_POINTS.MODERATE) {
    current = TEMPERATURES.MODERATE
    description = descriptionMessages.moderate
  } else if (latestMarketValue > MARKET_VALUE_BREAK_POINTS.LOW) {
    current = TEMPERATURES.LOW
    description = descriptionMessages.low
  } else {
    current = TEMPERATURES.VERY_LOW
    description = descriptionMessages.low
  }

  if (latestDelta >= RAPID_CHANGE_BREAK) {
    rate = VECTORS.UP_QUICKLY
  } else if (latestDelta >= CHANGE_BREAK) {
    rate = VECTORS.UP_NORMAL
  } else if (latestDelta * -1 >= RAPID_CHANGE_BREAK) {
    rate = VECTORS.DOWN_QUICKLY
  } else if (latestDelta * -1 >= CHANGE_BREAK) {
    rate = VECTORS.DOWN_NORMAL
  } else {
    rate = VECTORS.BALANCED
  }

  const statusMessage = intl.formatMessage(directionMessages[`${current}_${rate}`])
  const currentIcon = CURRENT_MARKET_ICONS[current]

  const finalText = `${currentIcon} ${zip} ${statusMessage}`
  const finalTextStart = finalText.split(' ').slice(0, -2).join(' ')
  const finalTextEnd = finalText.split(' ').slice(-2).join(' ')

  const finalDescription = intl.formatMessage(description)

  return (
    <div className={className}>
      <Text
        data-qa='market-spotlight-header-text'
        element='p'
        variant='neutralCool'
        variantCount='900'
        size={withDescription ? 'l' : null}
      >
        {finalTextStart}
        <span className={styles.forceWrapWithWord}>
          {' '}
          {finalTextEnd}
          <span className={styles.bumpRight}>
            <IconArrowRightBig className={classnames(styles.arrow, styles[rate])} />
            {rate === VECTORS.BALANCED && <IconArrowRightBig className={styles.arrow} />}
          </span>
        </span>
      </Text>
      <If condition={withDescription}>
        <Text size='s' variant='neutralCool' variantCount='700' className={styles.bumpDown}>
          {finalDescription}
        </Text>
      </If>
    </div>
  )
}

MarketSpotlightText.propTypes = {
  className: PropTypes.string,
  withDescription: PropTypes.bool
}

export default MarketSpotlightText
